import {
	AccountsScreen_GetAccounts_accounts_nodes as Account,
	SortEnumType,
	StakingEnrollmentFilterInput,
	StakingEnrollmentSortInput,
	StakingEnrollmentsScreen_GetStakingEnrollmentsVariables,
	StakingEnrollmentStatus,
} from '@app/codegen';
import { useTableState } from '@app/shared/components';
import { isFormEmpty } from '@app/shared/helpers';
import { useAppState } from '@itrustcapital/ui';
import { endOfDay, startOfDay } from 'date-fns';
import React from 'react';

import {
	FilterStakingEnrollmentsForm,
	useStakingEnrollmentsScreenFilterForm,
} from './StakingEnrollmentsForms';
import { useStakingEnrollmentsScreen_GetStakingEnrollmentsQuery } from './StakingEnrollmentsGraphql';

export type StakingEnrollmentsScreenState = ReturnType<typeof useStakingEnrollmentsScreenState>;

const StakingEnrollmentsScreenContext = React.createContext<
	StakingEnrollmentsScreenState | undefined
>(undefined);

export type StakingEnrollmentsScreenProviderProps = {
	value: StakingEnrollmentsScreenState | undefined;
	children: React.ReactNode;
};

export function useStakingEnrollmentsScreenState() {
	const filterStakingEnrollmentsForm = useStakingEnrollmentsScreenFilterForm();
	const { setError } = filterStakingEnrollmentsForm;

	const selectedRow = useAppState<Account | null>(null);
	const pauseQuery = useAppState(false);
	const statusType = useAppState<StakingEnrollmentStatus | null>(null);
	const defaultWhere: StakingEnrollmentFilterInput = {
		status: { neq: StakingEnrollmentStatus.NONE },
	};
	const tableState = useTableState<StakingEnrollmentSortInput, StakingEnrollmentFilterInput>({
		defaultSort: { createdAt: SortEnumType.DESC },
		defaultWhere,
		pagination: true,
		striped: true,
	});

	const variables: StakingEnrollmentsScreen_GetStakingEnrollmentsVariables = {
		where: tableState.where.get,
	};

	const stakingEnrollmentsQuery = useStakingEnrollmentsScreen_GetStakingEnrollmentsQuery({
		variables,
		onCompleted: (data) => {
			tableState.onCompleted(
				data.stakingEnrollments?.totalCount,
				data.stakingEnrollments?.pageInfo
			);
		},
		skip: pauseQuery.get,
	});

	function onSubmit(data: FilterStakingEnrollmentsForm) {
		pauseQuery.set(true);
		const where: StakingEnrollmentFilterInput = defaultWhere;

		statusType.set(data.status || null);
		tableState.reset();

		if (isFormEmpty(data)) {
			tableState.where.set(defaultWhere);
			pauseQuery.set(false);

			stakingEnrollmentsQuery.refetch?.({
				where: defaultWhere,
			});

			return;
		}

		where.status = data.status
			? {
					eq: data.status,
			  }
			: undefined;

		if (data.status) {
			where.status = {
				eq: data.status,
			};
		}

		if (data.search) {
			const searchTrimmed = data.search.trim();
			const andConditions: any[] = [];

			const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
			const numericalIdRegex = /^\d{1,7}$/;
			const accountNumbrRegex = /^(ITR|PCA|IC|TPA)\d+$/;

			if (accountNumbrRegex.test(searchTrimmed)) {
				andConditions.push({
					account: {
						accountNumber: {
							eq: searchTrimmed,
						},
					},
				});
			} else if (emailRegex.test(searchTrimmed)) {
				andConditions.push({
					user: {
						email: {
							eq: searchTrimmed,
						},
					},
				});
			} else if (numericalIdRegex.test(searchTrimmed)) {
				const digitOnlySearch = searchTrimmed.replaceAll(/\D/g, '');
				andConditions.push({
					id: {
						eq: +digitOnlySearch,
					},
				});
			}

			if (andConditions.length > 0) {
				where.and = andConditions;
			} else {
				setError('search', {
					message: 'Account numbers must start with ITR|IC|PCA.',
				});

				pauseQuery.set(true);

				return;
			}

			filterStakingEnrollmentsForm.setValue('search', searchTrimmed);
		}

		if (data.createdAt) {
			const whereDate = data.createdAt;
			where.createdAt = {
				gte: startOfDay(whereDate).toISOString(),
				lte: endOfDay(whereDate).toISOString(),
			};
		}

		tableState.where.set(where);
		pauseQuery.set(false);

		stakingEnrollmentsQuery.refetch?.({
			where,
		});
	}

	return {
		tableState,
		statusType,
		stakingEnrollmentsQuery,
		selectedRow,
		filterStakingEnrollmentsForm,
		pauseQuery,
		onSubmit,
	};
}

export function StakingEnrollmentsScreenProvider(
	props: StakingEnrollmentsScreenProviderProps | null
) {
	return (
		<StakingEnrollmentsScreenContext.Provider value={props?.value}>
			{props?.children}
		</StakingEnrollmentsScreenContext.Provider>
	);
}

export function useStakingEnrollmentsScreenContext() {
	const context = React.useContext(StakingEnrollmentsScreenContext);

	if (context === undefined) {
		throw new Error(
			'useStakingEnrollmentsScreenContext must be used within a StakingEnrollmentsScreenProvider'
		);
	}

	return context;
}
